import { Document, Page, pdf, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import React from 'react';
import createdWithDailyTools from '../assets/img/created-with-daily-tools.png';

export function calculateLoanDetails(loanAmount, interestRate, loanTerm) {
    const principal = parseFloat(loanAmount);
    const monthlyInterestRate = parseFloat(interestRate) / 100 / 12;
    const numberOfPayments = parseInt(loanTerm) * 12;

    const monthlyPayment = (principal * monthlyInterestRate) / (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments));
    const totalPayment = monthlyPayment * numberOfPayments;
    const totalInterest = totalPayment - principal;

    return {
        monthlyPayment: monthlyPayment.toFixed(2),
        totalPayment: totalPayment.toFixed(2),
        totalInterest: totalInterest.toFixed(2)
    };
}

export function generateAmortizationSchedule(loanAmount, interestRate, loanTerm) {
    const principal = parseFloat(loanAmount);
    const monthlyInterestRate = parseFloat(interestRate) / 100 / 12;
    const numberOfPayments = parseInt(loanTerm) * 12;

    const monthlyPayment = (principal * monthlyInterestRate) / (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments));
    let balance = principal;
    const schedule = [];

    for (let month = 1; month <= numberOfPayments; month++) {
        const interestPayment = balance * monthlyInterestRate;
        const principalPayment = monthlyPayment - interestPayment;
        balance -= principalPayment;

        schedule.push({
            month,
            payment: monthlyPayment.toFixed(2),
            principal: principalPayment.toFixed(2),
            interest: interestPayment.toFixed(2),
            balance: balance.toFixed(2)
        });
    }

    return schedule;
}

export const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    SEK: 'kr',
    NZD: 'NZ$',
    MXN: '$',
    SGD: 'S$',
    HKD: 'HK$',
    NOK: 'kr',
    KRW: '₩',
    TRY: '₺',
    RUB: '₽',
    BRL: 'R$',
    ZAR: 'R'
};

const styles = StyleSheet.create({
    body: {
        padding: 72, // 1 inch = 72 points
        margin: 0,
    },
    table: {
        display: "table",
        width: "auto",
        borderStyle: "solid",
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        margin: "auto",
        flexDirection: "row",
        height: "auto", // Automatically adjust height based on content
    },
    tableCol: {
        width: "20%",
        borderStyle: "solid",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
    },
    tableCell: {
        margin: "auto",
        marginTop: 5,
        fontSize: 10,
    },
    footer: {
        marginTop: 10,
        fontSize: 10,
        textAlign: 'right',
    },
});

export async function exportAmortizationReportAsPDF(reportData, fileName) {
    const AmortizationReport = (
        <Document>
            <Page style={styles.body} size="A4" orientation="landscape">
                <View>
                    <Text>Loan Amount: {reportData.loanAmount}</Text>
                    <Text>Annual Interest Rate: {reportData.interestRate}%</Text>
                    <Text>Loan Term: {reportData.loanTerm} years</Text>
                    <Text>Currency: {reportData.currency}</Text>
                </View>
                <View style={styles.table}>
                    <View style={styles.tableRow}>
                        <View style={styles.tableCol}><Text style={styles.tableCell}>Month</Text></View>
                        <View style={styles.tableCol}><Text style={styles.tableCell}>Payment</Text></View>
                        <View style={styles.tableCol}><Text style={styles.tableCell}>Principal</Text></View>
                        <View style={styles.tableCol}><Text style={styles.tableCell}>Interest</Text></View>
                        <View style={styles.tableCol}><Text style={styles.tableCell}>Balance</Text></View>
                    </View>
                    {reportData.schedule.map((row, index) => (
                        <View style={styles.tableRow} key={index}>
                            <View style={styles.tableCol}><Text style={styles.tableCell}>{row.month}</Text></View>
                            <View style={styles.tableCol}><Text style={styles.tableCell}>{row.payment}</Text></View>
                            <View style={styles.tableCol}><Text style={styles.tableCell}>{row.principal}</Text></View>
                            <View style={styles.tableCol}><Text style={styles.tableCell}>{row.interest}</Text></View>
                            <View style={styles.tableCol}><Text style={styles.tableCell}>{row.balance}</Text></View>
                        </View>
                    ))}
                </View>
                <Text style={styles.footer}>
                    <Image src={createdWithDailyTools} style={{ width: 100, height: 50 }} />
                </Text>
            </Page>
        </Document>
    );
    const pdfDoc = await pdf(AmortizationReport).toBlob();
    saveAs(pdfDoc, fileName);
}
